import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { map, Observable } from 'rxjs';

import { SortingConfig } from '@customer-portal/shared/models';

import {
  ClearAllUnsavedFilters,
  ClearUnsavedFilters,
  ClearUnsavedPaginationStartIndex,
  SetUnsavedFilters,
  SetUnsavedPaginationStartIndex,
  SetUnsavedSorting,
} from '../actions/unsaved-filters.actions';
import { UnsavedFiltersSelectors } from '../selectors/unsaved-filters.selectors';
import { UnsavedFiltersStateModel } from '../unsaved-filters.state';

@Injectable({ providedIn: 'root' })
export class UnsavedFiltersStoreService {
  private store = inject(Store);

  get allUnsavedFilters(): Signal<UnsavedFiltersStateModel> {
    return this.store.selectSignal(UnsavedFiltersSelectors.allUnsavedFilters);
  }

  get allUnsavedFilters$(): Observable<UnsavedFiltersStateModel> {
    return this.store.select(UnsavedFiltersSelectors.allUnsavedFilters);
  }

  get hasUnsavedFilters(): Signal<boolean> {
    return this.store.selectSignal(UnsavedFiltersSelectors.hasUnsavedFilters);
  }

  get hasUnsavedFilters$(): Observable<boolean> {
    return this.store.select(UnsavedFiltersSelectors.hasUnsavedFilters);
  }

  getFiltersByPage(pageKey: string): any {
    return this.store.selectSnapshot(UnsavedFiltersSelectors.getFiltersByPage)(
      pageKey,
    );
  }

  getGridStateByPage(pageKey: string) {
    return this.store.selectSnapshot(
      UnsavedFiltersSelectors.getGridStateByPage,
    )(pageKey);
  }

  getPaginationStartIndexByPage(pageKey: string): number | null {
    return this.store.selectSnapshot(
      UnsavedFiltersSelectors.getPaginationStartIndex,
    )(pageKey);
  }

  getFiltersByPage$(pageKey: string): Observable<any> {
    return this.store
      .select(UnsavedFiltersSelectors.getFiltersByPage)
      .pipe(map((filters) => filters(pageKey)));
  }

  hasUnsavedFiltersForPage(pageKey: string): boolean {
    return this.store.selectSnapshot(
      UnsavedFiltersSelectors.hasUnsavedFiltersForPage,
    )(pageKey);
  }

  @Dispatch()
  setUnsavedFilters = (pageKey: string, filters: any) =>
    new SetUnsavedFilters(pageKey, filters);

  @Dispatch()
  setUnsavedPaginationStartIndex = (pageKey: string, startIndex: number) =>
    new SetUnsavedPaginationStartIndex(pageKey, startIndex);

  @Dispatch()
  setUnsavedSorting = (pageKey: string, sorting: SortingConfig) =>
    new SetUnsavedSorting(pageKey, sorting);

  @Dispatch()
  clearUnsavedFilters = (pageKey: string) => new ClearUnsavedFilters(pageKey);

  @Dispatch()
  clearUnsavedPaginationStartIndex = (pageKey: string) =>
    new ClearUnsavedPaginationStartIndex(pageKey);

  @Dispatch()
  clearAllUnsavedFilters = () => new ClearAllUnsavedFilters();
}
